// 상세 현황

import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { MoreHorizontal, FileText, Search } from 'lucide-react'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import Tag from '@/components/ui/Tag'
import Alert from '@/components/ui/Alert'
import { designTokens } from '@/utils/design-tokens'
import type { ColDef, RowClickedEvent, ICellRendererParams, RowClassParams } from 'ag-grid-community'
import type { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { fetchBusinesses } from '@/store/businessSlice'
import { fetchTaskSummary } from '@/store/taskSummarySlice'
import { fetchTasks, fetchMoreTasks, resetTaskData } from '@/store/taskSlice'
import TaskDetailPanel from '@/pages/project/TaskManagement/components/TaskDetailPanel'
import TaskItemCreatePanel from '@/pages/project/TaskManagement/components/TaskItemCreatePanel'
import TaskCreatePanel from '@/pages/project/TaskManagement/components/TaskCreatePanel'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import MemberSelect from '@/components/ui/MemberSelect'
import TaskItemsTable from './components/TaskItemsTable'
import TaskBudgetsTable from './components/TaskBudgetsTable'
import TaskPerformancesTable from './components/TaskPerformancesTable'
import TasksTable from './components/TasksTable'
import TaskItemsListPanel from './components/TaskItemsListPanel'
import TasksListPanel from './components/TasksListPanel'
import TaskBudgetsListPanel from './components/TaskBudgetsListPanel'
import TaskPerformancesListPanel from './components/TaskPerformancesListPanel'
import ListViewTabPanel from './components/ListViewTabPanel'
import BusinessDetailPanel from './components/BusinessDetailPanel'
import DepartmentDetailPanel from './components/DepartmentDetailPanel'
import ReportDetailPanel from '@/pages/project/ReportManagement/components/ReportDetailPanel'
import DateRangePicker from '@/components/ui/DateRangePicker'
import { getInitialDateRange, formatDateForAPI, loadDateRangeFromStorage, parseDateFromStorage } from '@/components/ui/DateRangePicker/utils'
import Chart from '@/components/data/Chart'
import {
  TaskSummaryMonthlyResponse,
  TaskSummaryService,
  TaskSummaryTotalBusinessItem,
  TaskSummaryTotalDepartmentItem, TaskSummaryTotalResponse,
  TaskReportService
} from '@/api'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import { usePersistedBusinessFilter } from '@/hooks/usePersistedBusinessFilter'
import { useShowTasks } from '@/hooks/useShowTasks'
import StatusTables from '../DetailStatusDashboard/components/StatusTables'

interface DetailDashboardPageProps {
  /** 사업별/부서별 현황을 진행률 막대 표로 보인다 (새 상세 현황 메뉴) */
  groupedStatus?: boolean
}

const DetailDashboardPage: React.FC<DetailDashboardPageProps> = ({ groupedStatus = false }) => {
  // Redux selectors and dispatch
  const dispatch = useAppDispatch()
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 요약 행·탭·사업별/부서별 현황 열을 숨긴다
  const showTasks = useShowTasks()
  const { businesses, isLoading: businessLoading, error: businessError } = useAppSelector(state => state.business)
  const { summary: taskSummary, isLoading: isSummaryLoading, error: summaryError } = useAppSelector(state => state.taskSummary)
  const {
    items: taskItems,
    isLoading: isLoadingTasks,
    isLoadingMore: isLoadingMoreTasks,
    error: taskReduxError,
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    hasNext,
    loadedPages
  } = useAppSelector((state) => state.task)

  // Selector state for filtering
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('')
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string>('')
  const {
    businessId: selectedBusinessId,
    businessName: selectedBusinessName,
    setBusiness: saveSelectedBusiness,
  } = usePersistedBusinessFilter()
  const [selectedMemberId, setSelectedMemberId] = useState<string>('')
  const [selectedMemberName, setSelectedMemberName] = useState<string>('')
  const [_selectedMemberEmail, setSelectedMemberEmail] = useState<string>('')

  // Period view state
  const [periodViewType, setPeriodViewType] = useState<'period' | 'all'>('period')
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<'period'>('period')

  // Date range picker state - initialize from localStorage
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const [initialStart] = getInitialDateRange()
    return initialStart
  })
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const [, initialEnd] = getInitialDateRange()
    return initialEnd
  })

  // State to trigger re-render when localStorage changes
  const [storageUpdateTrigger, setStorageUpdateTrigger] = useState(0)

  // Get display dates from localStorage (for DateRangePicker display)
  const displayDates = useMemo((): [Date | null, Date | null] => {
    try {
      const stored = loadDateRangeFromStorage()
      return [
        parseDateFromStorage(stored.start_date),
        parseDateFromStorage(stored.end_date)
      ]
    } catch {
      const [initialStart, initialEnd] = getInitialDateRange()
      return [initialStart, initialEnd]
    }
  }, [storageUpdateTrigger]) // Recalculate when storage update trigger changes

  // List view state
  const [selectedListType, setSelectedListType] = useState<'task' | 'work' | 'budget' | 'performance'>('work')

  // Search state
  const [searchInput, setSearchInput] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchTrigger, setSearchTrigger] = useState(0)  // Explicit search trigger

  // Task item status filter state
  const [taskItemStatusFilter, setTaskItemStatusFilter] = useState<string>('')

  // Task (세부실행과제) status filter state
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('')

  // 예산/성과 상태 필터 — 백엔드에서 2-state(incomplete/completed) 지원
  const [budgetStatusFilter, setBudgetStatusFilter] = useState<string>('')
  const [performanceStatusFilter, setPerformanceStatusFilter] = useState<string>('')

  // Business summary state
  const [businessSummaryData, setBusinessSummaryData] = useState<TaskSummaryTotalBusinessItem[]>([])
  const [isLoadingBusinessSummary, setIsLoadingBusinessSummary] = useState(false)
  const [businessSummaryError, setBusinessSummaryError] = useState<string | null>(null)

  // Department summary state
  const [departmentSummaryData, setDepartmentSummaryData] = useState<TaskSummaryTotalDepartmentItem[]>([])
  const [isLoadingDepartmentSummary, setIsLoadingDepartmentSummary] = useState(false)
  const [departmentSummaryError, setDepartmentSummaryError] = useState<string | null>(null)

  // Business detail side panel state
  const [isBusinessDetailPanelOpen, setIsBusinessDetailPanelOpen] = useState(false)
  const [selectedBusinessSummary, setSelectedBusinessSummary] = useState<TaskSummaryTotalBusinessItem | null>(null)
  // Department detail side panel state
  const [isDepartmentDetailPanelOpen, setIsDepartmentDetailPanelOpen] = useState(false)
  const [selectedDepartmentSummary, setSelectedDepartmentSummary] = useState<TaskSummaryTotalDepartmentItem | null>(null)

  // Alert state for business filter notification
  const [showBusinessFilterAlert, setShowBusinessFilterAlert] = useState(false)
  const [businessFilterAlertMessage, setBusinessFilterAlertMessage] = useState('')

  // Alert state for department filter notification
  const [showDepartmentFilterAlert, setShowDepartmentFilterAlert] = useState(false)
  const [departmentFilterAlertMessage, setDepartmentFilterAlertMessage] = useState('')

  // Task list state (managed by Redux)
  // Note: taskItems, isLoadingTasks, taskReduxError are now from Redux state

  // ===== 차트/요약 클릭 → 항목별 리스트 사이드패널 (전체탭 전용) =====
  // 한 번에 하나만 열림. 차트 봉/포인트를 정확히 클릭한 경우 dateRangeOverride로 해당 월(YYYY-MM-01 ~ 말일) 좁힘.
  type SummaryListPanelType = 'task_item' | 'task' | 'budget' | 'performance'
  type SummaryListPanelStatus = 'all' | 'completed' | 'incomplete'
  const [summaryListPanel, setSummaryListPanel] = useState<{
    type: SummaryListPanelType
    statusBucket: SummaryListPanelStatus
    title: string
    dateRangeOverride?: { start: Date; end: Date }
  } | null>(null)

  // ===== 사업별/부서별 현황 "목록보기" → 유형 통합(탭) 목록 사이드패널 =====
  // 클릭한 행의 사업/부서로 스코프를 잡고, 대시보드 상단 필터도 함께 lock으로 적용.
  const [listViewPanel, setListViewPanel] = useState<{
    scope: 'business' | 'department'
    id: string
    name: string
  } | null>(null)

  // ===== 상세 패널 — id만 보관, 데이터는 TaskDetailPanel이 자체 fetch =====
  // refreshKey를 올리면 패널이 자동 재조회 (수정 성공 후 사용)
  const [taskDetailRefreshKey, setTaskDetailRefreshKey] = useState(0)

  // Task detail panel state (type='task')
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)
  const [selectedTaskIdForDetail, setSelectedTaskIdForDetail] = useState<string | null>(null)

  // Task item detail panel state (type='task_item')
  const [isTaskItemDetailPanelOpen, setIsTaskItemDetailPanelOpen] = useState(false)
  const [selectedTaskItemId, setSelectedTaskItemId] = useState<string | null>(null)

  // Budget item detail panel state (type='task')
  const [isBudgetDetailPanelOpen, setIsBudgetDetailPanelOpen] = useState(false)
  const [selectedBudgetTaskId, setSelectedBudgetTaskId] = useState<string | null>(null)

  // Performance item detail panel state (type='task')
  const [isPerformanceDetailPanelOpen, setIsPerformanceDetailPanelOpen] = useState(false)
  const [selectedPerformanceTaskId, setSelectedPerformanceTaskId] = useState<string | null>(null)

  // Report detail panel state — 패널이 자체 fetch하므로 task + selectedTemplate만 보관
  const [reportPanelState, setReportPanelState] = useState({
    isOpen: false,
    selectedTask: null as TaskTreeNode | null,
    selectedTemplate: null as { key: string; name: string } | null,
  })

  // Action button click flag to prevent row click when action button is clicked
  const [isActionButtonClicked, setIsActionButtonClicked] = useState(false)

  // Report templates state
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)

  // Task item edit panel state
  const [isTaskItemEditPanelOpen, setIsTaskItemEditPanelOpen] = useState(false)
  const [editTaskItemData, setEditTaskItemData] = useState<{
    id: string
    name: string
    taskId: string
    taskName: string
    taskSubgroupName: string
    businessName: string
    startAt: string
    endAt: string
    output: string
  } | null>(null)
  // isRefreshingTask는 handleTasksDataRefreshed에서 "수정 후 새로고침 중" 여부 판단에 사용
  const [isRefreshingTask, setIsRefreshingTask] = useState(false)
  const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(0)
  // 수행업무/예산/성과 목록 새로고침 트리거 — 각 테이블이 자체 상태로 조회하므로
  // 부모는 값만 올려 재조회를 요청한다 (기존에는 부모가 전역 스토어를 직접 갱신했다)
  const [taskItemRefreshTrigger, setTaskItemRefreshTrigger] = useState(0)
  const [taskBudgetRefreshTrigger, setTaskBudgetRefreshTrigger] = useState(0)
  const [taskPerformanceRefreshTrigger, setTaskPerformanceRefreshTrigger] = useState(0)
  const [editSourcePanel, setEditSourcePanel] = useState<'task' | 'budget' | 'performance' | null>(null)

  // Task edit panel state
  const [isTaskEditPanelOpen, setIsTaskEditPanelOpen] = useState(false)
  const [editTaskData, setEditTaskData] = useState<{
    id: string
    name: string
    taskSubgroupId: string
    taskSubgroupName: string
    taskGroupName: string
    businessName: string
    businessId: string
    assigneeId: string
    assigneeName: string
    subAssigneeId: string
    subAssigneeName: string
    managerId: string
    managerName: string
    reviewerId: string
    reviewerName: string
    departmentId: string
    departmentName: string
    startAt: string
    endAt: string
    description: string
    labels?: Array<{ id: string; business_label_id: string; key: string }>
    detailInfos?: Array<{ id: string; business_detail_field_id: string; key: string; name: string; value: string }>
    extraInfos?: Array<{ id: string; business_extra_field_id: string; key: string; name: string; value: string }>
    /** AI 분석 프롬프트에 함께 실리는 판단근거 */
    judgmentBasis?: string
    /** 편집 패널의 budget/performance 초기값에 사용 (raw originalData) */
    taskOriginal?: any
  } | null>(null)

  // Monthly summary state for 전체 tab
  const [monthlySummary, setMonthlySummary] = useState<TaskSummaryMonthlyResponse | null>(null)
  const [isMonthlySummaryLoading, setIsMonthlySummaryLoading] = useState(false)
  const [monthlySummaryError, setMonthlySummaryError] = useState<string | null>(null)

  // Summary data interface
  interface SummaryDataItem {
    id: string
    itemName: '세부실행과제' | '수행업무' | '예산' | '성과'
    total: number
    completed: number
    incomplete: number
    ratio: number
    unit?: string
  }

  // API response to UI data mapping function for regular summary
  const mapApiToSummaryData = useCallback((apiResponse: TaskSummaryTotalResponse): SummaryDataItem[] => [
    {
      id: '1',
      itemName: '수행업무',
      total: apiResponse.task_item.total.total_count,
      completed: apiResponse.task_item.total.completed_count,
      incomplete: apiResponse.task_item.total.incomplete_count,
      ratio: apiResponse.task_item.total.count_percentage
    },
    {
      id: '2',
      itemName: '세부실행과제',
      total: apiResponse.task.total.total_count,
      completed: apiResponse.task.total.completed_count,
      incomplete: apiResponse.task.total.incomplete_count,
      ratio: apiResponse.task.total.count_percentage
    },
    {
      id: '3',
      itemName: '예산',
      total: apiResponse.task_budget.total.total_target_figure,
      completed: apiResponse.task_budget.total.total_done_figure,
      incomplete: apiResponse.task_budget.total.total_left_figure,
      ratio: apiResponse.task_budget.total.figure_percentage,
      unit: '원'
    },
    {
      id: '4',
      itemName: '성과',
      total: apiResponse.task_performance.total.total_count,
      completed: apiResponse.task_performance.total.completed_count,
      incomplete: apiResponse.task_performance.total.incomplete_count,
      ratio: apiResponse.task_performance.total.count_percentage
    }
  ], [])

  // Monthly API response to UI data mapping function for 전체 tab
  const mapMonthlyApiToSummaryData = useCallback((apiResponse: TaskSummaryMonthlyResponse): SummaryDataItem[] => [
    {
      id: '1',
      itemName: '수행업무',
      total: apiResponse.task_item.total.total_count,
      completed: apiResponse.task_item.total.completed_count,
      incomplete: apiResponse.task_item.total.incomplete_count,
      ratio: apiResponse.task_item.total.count_percentage
    },
    {
      id: '2',
      itemName: '세부실행과제',
      total: apiResponse.task.total.total_count,
      completed: apiResponse.task.total.completed_count,
      incomplete: apiResponse.task.total.incomplete_count,
      ratio: apiResponse.task.total.count_percentage
    },
    {
      id: '3',
      itemName: '예산',
      total: apiResponse.task_budget.total.total_target_figure,
      completed: apiResponse.task_budget.total.total_done_figure,
      incomplete: apiResponse.task_budget.total.total_left_figure,
      ratio: apiResponse.task_budget.total.figure_percentage,
      unit: '원'
    },
    {
      id: '4',
      itemName: '성과',
      total: apiResponse.task_performance.total.total_count,
      completed: apiResponse.task_performance.total.completed_count,
      incomplete: apiResponse.task_performance.total.incomplete_count,
      ratio: apiResponse.task_performance.total.count_percentage
    }
  ], [])

  // ===== 차트 클릭 핸들러 (전체탭 전용) =====
  // ApexCharts는 옵션 객체가 새로 생성돼도 이벤트 핸들러를 항상 재바인딩하지 않아 stale closure가 남는다.
  // 각 차트별 "최신 click 핸들러"를 ref로 보관하고, ApexCharts 이벤트는 항상 ref.current를 호출만 한다.
  const taskChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)
  const budgetChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)
  const performanceChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)

  const chartTypeToPanelType = useMemo(() => ({
    work: 'task_item',
    task: 'task',
    budget: 'budget',
    performance: 'performance',
  } as const), [])

  const itemNameByListType = useMemo(() => ({
    task_item: '수행업무',
    task: '세부실행과제',
    budget: '예산',
    performance: '성과',
  } as const), [])

  const closeSummaryListPanel = useCallback(() => setSummaryListPanel(null), [])

  const closeListViewPanel = useCallback(() => setListViewPanel(null), [])

  // statusBucket → 테이블에 전달할 status 필터로 매핑
  const resolveStatusFilter = useCallback(
    (type: SummaryListPanelType, bucket: SummaryListPanelStatus): string | undefined => {
      if (bucket === 'all') return undefined
      if (type === 'task_item') return bucket === 'completed' ? 'completed' : 'incomplete'
      if (type === 'task') return bucket === 'completed' ? 'completed' : 'incomplete'
      return undefined
    },
    [],
  )

  // 요약 그리드의 전체/완료/미완료 셀 클릭 → 해당 리스트 사이드패널 열기 (전체탭 전용)
  const handleSummaryCellClicked = useCallback((event: any) => {
    if (periodViewType !== 'all') return
    const itemName = event.data?.itemName as '세부실행과제' | '수행업무' | '예산' | '성과' | undefined
    const colId = event.column?.getColId?.() as string | undefined
    if (!itemName || !colId) return

    const bucketByCol: Record<string, SummaryListPanelStatus | undefined> = {
      totalFormatted: 'all',
      completedFormatted: 'completed',
      incompleteFormatted: 'incomplete',
    }
    const statusBucket = bucketByCol[colId]
    if (!statusBucket) return

    const typeByName: Record<'세부실행과제' | '수행업무' | '예산' | '성과', SummaryListPanelType> = {
      '수행업무': 'task_item',
      '세부실행과제': 'task',
      '예산': 'budget',
      '성과': 'performance',
    }
    const type = typeByName[itemName]
    const bucketLabel = statusBucket === 'all' ? '전체' : statusBucket === 'completed' ? '완료' : '미완료'
    setSummaryListPanel({
      type,
      statusBucket,
      title: `${itemName} 목록 (${bucketLabel})`,
    })
  }, [periodViewType])

  // 차트의 특정 봉/포인트 클릭 → 해당 월(monthKey: YYYY-MM)로 좁힘
  const handleChartPointClick = useCallback((
    chartType: 'task' | 'work' | 'budget' | 'performance',
    monthKey: string | undefined,
  ) => {
    const type = chartTypeToPanelType[chartType]
    if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) {
      setSummaryListPanel({
        type,
        statusBucket: 'all',
        title: `${itemNameByListType[type]} 목록 (전체)`,
      })
      return
    }
    const [yearStr, monthStr] = monthKey.split('-')
    const year = parseInt(yearStr, 10)
    const monthIdx = parseInt(monthStr, 10) - 1
    const start = new Date(year, monthIdx, 1)
    const end = new Date(year, monthIdx + 1, 1)
    end.setMilliseconds(end.getMilliseconds() - 1)
    setSummaryListPanel({
      type,
      statusBucket: 'all',
      title: `${itemNameByListType[type]} 목록 (${year}년 ${monthIdx + 1}월)`,
      dateRangeOverride: { start, end },
    })
  }, [chartTypeToPanelType, itemNameByListType])

  const buildChartClickHandler = useCallback((
    chartType: 'task' | 'work' | 'budget' | 'performance',
    monthKeys: string[],
  ) => {
    return (idx: number) => {
      // 월별 봉/포인트 또는 x축 월 라벨을 정확히 클릭했을 때만 해당 월 목록을 연다.
      // 그 외 차트 빈 영역 클릭은 무시한다 — 기간 필터 없는 목록이 열리면 사용자가 오류로 인지한다.
      if (idx >= 0 && monthKeys[idx]) {
        handleChartPointClick(chartType, monthKeys[idx])
      }
    }
  }, [handleChartPointClick])

  // Chart data transformation for monthly task data
  const transformMonthlyDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [] as string[],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '완료', type: 'column', data: [] },
          { name: '완료율', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const incompleteData = sortedMonths.map(month => {
      const value = monthlyData[month]?.incomplete_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    // Calculate total data (completed + incomplete)
    const totalData = completedData.map((completed, index) => completed + incompleteData[index])

    return {
      categories,
      monthKeys: sortedMonths,
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '완료',
          type: 'column',
          data: completedData
        },
        {
          name: '완료율',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task budget data
  const transformMonthlyTaskBudgetDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [] as string[],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '집행', type: 'column', data: [] },
          { name: '집행률', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_done_figure
      return typeof value === 'number' ? value : 0
    })
    const remainingData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_left_figure
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.figure_percentage
      return typeof value === 'number' ? value : 0
    })

    // Calculate total data (completed + remaining)
    const totalData = completedData.map((completed, index) => completed + remainingData[index])

    return {
      categories,
      monthKeys: sortedMonths,
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '집행',
          type: 'column',
          data: completedData
        },
        {
          name: '집행률',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task performance data
  const transformMonthlyTaskPerformanceDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [] as string[],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '달성', type: 'column', data: [] },
          { name: '달성률', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const incompleteData = sortedMonths.map(month => {
      const value = monthlyData[month]?.incomplete_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    // Calculate total data (completed + incomplete)
    const totalData = completedData.map((completed, index) => completed + incompleteData[index])

    return {
      categories,
      monthKeys: sortedMonths,
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '달성',
          type: 'column',
          data: completedData
        },
        {
          name: '달성률',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart options for stacked bar + line chart - OverallDashboard pattern
  const getChartOptions = useCallback((
    categories: string[],
    chartType: 'task' | 'work' | 'budget' | 'performance' = 'task',
    seriesData?: any[],
    handlerRef?: React.MutableRefObject<((dataPointIndex: number) => void) | null>,
  ) => {
    // Get chart type specific configurations
    const getChartConfig = () => {
      switch (chartType) {
        case 'task':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '세부실행과제 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
        case 'work':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '수행업무 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
        case 'budget':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '집행',
            thirdSeriesName: '집행률',
            yAxisTitle: '예산 금액',
            percentageTitle: '집행률 (%)',
            unit: '원'
          }
        case 'performance':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '달성',
            thirdSeriesName: '달성률',
            yAxisTitle: '성과 과제 수',
            percentageTitle: '달성률 (%)',
            unit: '개'
          }
        default:
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '세부실행과제 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
      }
    }

    const config = getChartConfig()

    // Calculate max value from both column series to sync Y-axis scales
    let sharedMax: number | undefined
    if (seriesData && seriesData.length >= 2) {
      const series0Data = seriesData[0]?.data || []
      const series1Data = seriesData[1]?.data || []
      const max0 = Math.max(...series0Data, 0)
      const max1 = Math.max(...series1Data, 0)
      const rawMax = Math.max(max0, max1)
      // Round up to nearest 10 (e.g., 93 -> 100, 141 -> 150)
      sharedMax = Math.ceil(rawMax / 100) * 100
    }

    return {
      chart: {
        type: 'line' as const,
        stacked: false,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        events: {
          // 차트 SVG 영역 어디든 클릭하면 발화. dataPointIndex === -1(봉/포인트가 아닌 영역)이면
          // 핸들러가 아무 목록도 열지 않는다. x축 월 라벨은 아래 xAxisLabelClick이 처리한다.
          click: (event: any, _chartContext: any, config: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof config?.dataPointIndex === 'number' ? config.dataPointIndex : -1
            handlerRef?.current?.(idx)
          },
          // 봉/마커를 정확히 클릭했을 때만 발화 — 안전망으로 함께 둔다.
          dataPointSelection: (event: any, _chartContext: any, config: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof config?.dataPointIndex === 'number' ? config.dataPointIndex : -1
            if (idx >= 0) handlerRef?.current?.(idx)
          },
          // x축 월 라벨 클릭 → 봉/포인트 클릭과 동일하게 해당 월로 좁힌 목록을 연다.
          // labelIndex는 categories 인덱스이고 monthKeys와 1:1로 대응한다.
          xAxisLabelClick: (event: any, _chartContext: any, opts: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof opts?.labelIndex === 'number' ? opts.labelIndex : -1
            if (idx >= 0) handlerRef?.current?.(idx)
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
        },
      },
      stroke: {
        width: [0, 0, 3],
        curve: 'smooth' as const,
      },
      xaxis: {
        categories: categories,
        title: {
          text: '월별',
          style: {
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            color: designTokens.colors.gray[5],
          },
        },
        labels: {
          style: {
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px',
            colors: designTokens.colors.gray[4],
            // 월 라벨도 클릭하면 해당 월 목록이 열리므로 클릭 가능 커서를 표시한다 (globals.css)
            cssClass: 'dot-clickable-xaxis-label',
          },
        },
      },
      yaxis: [
        {
          seriesName: config.firstSeriesName,
          title: {
            text: config.yAxisTitle,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[5],
            },
          },
          labels: {
            formatter: (value: number) => {
              if (chartType === 'budget') {
                return `${Math.round(value).toLocaleString()}원`
              }
              return `${value.toLocaleString()}${config.unit}`
            },
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '12px',
              colors: designTokens.colors.gray[4],
            },
          },
          ...(sharedMax !== undefined && { max: sharedMax, min: 0, stepSize:10, tickAmount:5, forceNiceScale: true }),
        },
        {
          seriesName: config.secondSeriesName,
          show: false,
          ...(sharedMax !== undefined && { max: sharedMax, min: 0, stepSize:10, tickAmount:5, forceNiceScale: true }),
        },
        {
          seriesName: config.thirdSeriesName,
          opposite: true,
          title: {
            text: config.percentageTitle,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[5],
            },
          },
          labels: {
            formatter: (value: number) => `${value.toFixed(1)}%`,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '12px',
              colors: designTokens.colors.gray[4],
            },
          },
          ...({ max: 100, min: 0, stepSize:10, tickAmount:10, forceNiceScale: true }),
        },
      ],
      colors: [designTokens.colors.gray[3], designTokens.colors.primary.dark, designTokens.colors.warning.dark],
      legend: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '12px',
        position: 'top' as const,
        horizontalAlign: 'center' as const,
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '12px',
        },
        y: {
          formatter: (value: number, { seriesIndex }: any) => {
            if (seriesIndex === 2) {
              return `${value.toFixed(1)}%`
            }
            if (chartType === 'budget') {
              return `${Math.round(value).toLocaleString()}원`
            }
            return `${value.toLocaleString()}${config.unit}`
          },
        },
      },
      grid: {
        borderColor: designTokens.colors.gray[2],
      },
    }
  }, [])

  // Summary data preparation with API integration
  const summaryData = useMemo((): SummaryDataItem[] => {
    let rows: SummaryDataItem[]
    if (periodViewType === 'all') {
      // For 전체 tab, use monthly summary if available
      rows = isMonthlySummaryLoading || !monthlySummary ? [] : mapMonthlyApiToSummaryData(monthlySummary)
    } else {
      // For 기간별 tab, use regular summary
      rows = isSummaryLoading || !taskSummary ? [] : mapApiToSummaryData(taskSummary)
    }
    return showTasks ? rows : rows.filter(row => row.itemName !== '세부실행과제')
  }, [periodViewType, taskSummary, isSummaryLoading, monthlySummary, isMonthlySummaryLoading, mapApiToSummaryData, mapMonthlyApiToSummaryData, showTasks])

  // 세부실행과제 표시가 꺼지면 선택돼 있던 세부실행과제 탭에서 수행업무 탭으로 옮긴다
  useEffect(() => {
    if (!showTasks && selectedListType === 'task') {
      setSelectedListType('work')
    }
  }, [showTasks, selectedListType])

  // Load businesses when workspace changes
  useEffect(() => {
    if (selectedWorkspace?.id) {
      dispatch(fetchBusinesses({
        workspaceId: selectedWorkspace.id,
        page: 1,
        perPage: 100
      }))
    }
  }, [selectedWorkspace?.id, dispatch])

  // Fetch monthly summary for 전체 tab
  const fetchMonthlySummary = useCallback(async () => {
    if (!selectedWorkspace?.id) {
      setMonthlySummaryError('워크스페이스가 선택되지 않았습니다.')
      return
    }

    setIsMonthlySummaryLoading(true)
    setMonthlySummaryError(null)

    try {
      const filters = {
        workspaceId: selectedWorkspace.id,
        departmentId: selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : undefined,
        businessId: selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : undefined,
        assigneeId: selectedMemberId && selectedMemberName !== '전체' ? selectedMemberId : undefined,
        startDate: periodViewType === 'period' && startDate ? formatDateForAPI(startDate) : undefined,
        endDate: periodViewType === 'period' && endDate ? formatDateForAPI(endDate) : undefined
      }

      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryMonthly(filters)
      setMonthlySummary(response)
    } catch (err) {
      console.error('Failed to fetch monthly summary:', err)
      setMonthlySummaryError('월별 요약 정보를 불러오는데 실패했습니다.')
    } finally {
      setIsMonthlySummaryLoading(false)
    }
  }, [
    selectedWorkspace?.id,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    selectedMemberId,
    selectedMemberName,
    periodViewType,
    startDate,
    endDate
  ])

  // Fetch task summary when workspace or filters change (기간별 tab only)
  useEffect(() => {
    // Only call regular summary API for 기간별 tab (not 전체 tab)
    if (periodViewType === 'all' || !selectedWorkspace?.id) {
      return
    }

    const filters = {
      workspaceId: selectedWorkspace.id,
      departmentId: selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : null,
      businessId: selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : null,
      assigneeId: selectedMemberId && selectedMemberName !== '전체' ? selectedMemberId : null,
      startDate: periodViewType === 'period' && startDate ? formatDateForAPI(startDate) : null,
      endDate: periodViewType === 'period' && endDate ? formatDateForAPI(endDate) : null
    }

    dispatch(fetchTaskSummary(filters))
  }, [
    selectedWorkspace?.id,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    selectedMemberId,
    selectedMemberName,
    startDate,
    endDate,
    periodViewType,
    dispatch
  ])

  // Fetch monthly summary when in 전체 tab
  useEffect(() => {
    if (periodViewType === 'all' && selectedWorkspace?.id) {
      fetchMonthlySummary()
    }
  }, [
    periodViewType,
    selectedWorkspace?.id,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    selectedMemberId,
    selectedMemberName,
    startDate,
    endDate,
    fetchMonthlySummary
  ])

  // Selector handlers
  const handleDepartmentSelect = (departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)

    // Reset member selection when department changes
    setSelectedMemberId('')
    setSelectedMemberName('')
    setSelectedMemberEmail('')

    // Trigger data refresh with new filter
    if (selectedListType === 'task') {
      fetchTaskData()
    }
  }

  const handleBusinessSelect = (businessId: string, businessName: string) => {
    saveSelectedBusiness(businessId, businessName)

    // Trigger data refresh with new filter
    if (selectedListType === 'task') {
      fetchTaskData()
    }
  }

  const handleMemberSelect = (memberId: string, memberName: string, memberEmail: string) => {
    setSelectedMemberId(memberId)
    setSelectedMemberName(memberName)
    setSelectedMemberEmail(memberEmail)

    // Trigger data refresh with new filter
    if (selectedListType === 'task') {
      fetchTaskData()
    }
  }

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedDepartmentId('')
    setSelectedDepartmentName('')
    saveSelectedBusiness('', '')
    setSelectedMemberId('')
    setSelectedMemberName('')
    setSelectedMemberEmail('')

    // Trigger data refresh
    if (selectedListType === 'task') {
      fetchTaskData()
    }
  }

  // const handleMoreOptions = () => {
  //   console.warn('더보기 기능 미구현')
  // }

  const handlePeriodViewChange = (viewType: 'period' | 'all') => {
    setPeriodViewType(viewType)
  }

  const handleTimePeriodChange = (period: 'daily' | 'weekly' | 'monthly') => {
    setSelectedTimePeriod(period)
    // Clear current date selection when period changes
    setStartDate(null)
    setEndDate(null)
  }


  const handleSearch = useCallback(() => {
    setSearchQuery(searchInput.trim())
    setSearchTrigger(prev => prev + 1)  // Trigger search execution
  }, [searchInput])

  const handleListTypeChange = (listType: 'task' | 'work' | 'budget' | 'performance') => {
    setSelectedListType(listType)
    // Clear search when switching tabs
    setSearchInput('')
    setSearchQuery('')
  }

  // Fetch task data with filters
  const fetchTaskData = useCallback(async () => {
    if (selectedListType !== 'task') return
    if (!selectedWorkspace?.id) {
      return
    }

    // Build filter parameters
    const params: any = {
      workspaceId: selectedWorkspace.id,
      page: 1,
      perPage: pageSize,
      reset: true
    }

    // Add date range parameters (only in period mode)
    if (periodViewType === 'period' && startDate) {
      params.startDate = formatDateForAPI(startDate)
    }
    if (periodViewType === 'period' && endDate) {
      params.endDate = formatDateForAPI(endDate)
    }

    // Add filters if selected (exclude if "전체" is selected)
    if (selectedDepartmentId && selectedDepartmentName !== '전체') {
      params.departmentId = selectedDepartmentId
    }
    if (selectedBusinessId && selectedBusinessName !== '전체') {
      params.businessId = selectedBusinessId
    }
    if (selectedMemberId && selectedMemberName !== '전체') {
      params.assigneeId = selectedMemberId
    }

    dispatch(fetchTasks(params))
  }, [
    dispatch,
    selectedListType,
    selectedWorkspace?.id,
    pageSize,
    periodViewType,
    startDate,
    endDate,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    selectedMemberId,
    selectedMemberName
  ])

  // Reset task data when filters change
  useEffect(() => {
    if (selectedListType === 'task') {
      dispatch(resetTaskData())
    }
  }, [
    dispatch,
    selectedListType,
    selectedWorkspace?.id,
    selectedDepartmentId,
    selectedBusinessId,
    selectedMemberId,
    startDate,
    endDate
  ])

  // Fetch business summary data
  const fetchBusinessSummaryData = useCallback(async () => {
    if (!selectedWorkspace?.id) {
      setBusinessSummaryError('워크스페이스가 선택되지 않았습니다.')
      return
    }

    setIsLoadingBusinessSummary(true)
    setBusinessSummaryError(null)

    try {
      // Build filter parameters
      const params: any = {
        workspaceId: selectedWorkspace.id,
        page: 1,
        perPage: 100,
      }

      // Add date range parameters (only in period mode)
      if (periodViewType === 'period' && startDate) {
        params.startDate = formatDateForAPI(startDate)
      }
      if (periodViewType === 'period' && endDate) {
        params.endDate = formatDateForAPI(endDate)
      }

      // Add filter parameters
      if (selectedDepartmentId && selectedDepartmentName !== '전체') {
        params.departmentId = selectedDepartmentId
      }
      if (selectedBusinessId && selectedBusinessName !== '전체') {
        params.businessId = selectedBusinessId
      }
      if (selectedMemberId && selectedMemberName !== '전체') {
        params.assigneeId = selectedMemberId
      }

      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryTotalByBusiness(params)

      if (response) {
        setBusinessSummaryData(response)
      } else {
        setBusinessSummaryData([])
      }
    } catch (err) {
      console.error('Failed to fetch business summary data:', err)
      setBusinessSummaryError('사업별 현황 데이터를 불러오는데 실패했습니다.')
      setBusinessSummaryData([])
    } finally {
      setIsLoadingBusinessSummary(false)
    }
  }, [selectedWorkspace?.id, periodViewType, startDate, endDate, selectedDepartmentId, selectedDepartmentName, selectedBusinessId, selectedBusinessName, selectedMemberId, selectedMemberName])

  // Fetch department summary data
  const fetchDepartmentSummaryData = useCallback(async () => {
    if (!selectedWorkspace?.id) {
      setDepartmentSummaryError('워크스페이스가 선택되지 않았습니다.')
      return
    }

    setIsLoadingDepartmentSummary(true)
    setDepartmentSummaryError(null)

    try {
      // Build filter parameters
      const params: any = {
        workspaceId: selectedWorkspace.id,
        page: 1,
        perPage: 100,
      }

      // Add date range parameters (only in period mode)
      if (periodViewType === 'period' && startDate) {
        params.startDate = formatDateForAPI(startDate)
      }
      if (periodViewType === 'period' && endDate) {
        params.endDate = formatDateForAPI(endDate)
      }

      // Add filter parameters
      if (selectedDepartmentId && selectedDepartmentName !== '전체') {
        params.departmentId = selectedDepartmentId
      }
      if (selectedBusinessId && selectedBusinessName !== '전체') {
        params.businessId = selectedBusinessId
      }
      if (selectedMemberId && selectedMemberName !== '전체') {
        params.assigneeId = selectedMemberId
      }

      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryTotalByDepartment(params)

      if (response) {
        setDepartmentSummaryData(response)
      } else {
        setDepartmentSummaryData([])
      }
    } catch (err) {
      console.error('Failed to fetch department summary data:', err)
      setDepartmentSummaryError('부서별 현황 데이터를 불러오는데 실패했습니다.')
      setDepartmentSummaryData([])
    } finally {
      setIsLoadingDepartmentSummary(false)
    }
  }, [selectedWorkspace?.id, periodViewType, startDate, endDate, selectedDepartmentId, selectedDepartmentName, selectedBusinessId, selectedBusinessName, selectedMemberId, selectedMemberName])

  // Effect to fetch data when list type changes to task, workspace changes, or date range changes, or filters change
  // Only fetch task data in 'period' mode, not in 'all' mode
  useEffect(() => {
    if (selectedListType === 'task' && periodViewType === 'period') {
      fetchTaskData()
    }
  }, [selectedListType, selectedWorkspace?.id, startDate, endDate, periodViewType, selectedDepartmentId, selectedBusinessId, selectedMemberId, fetchTaskData])

  // Effect to fetch business summary data when dependencies change
  useEffect(() => {
    fetchBusinessSummaryData()
  }, [selectedWorkspace?.id, startDate, endDate, periodViewType, selectedDepartmentId, selectedBusinessId, selectedMemberId, fetchBusinessSummaryData])

  // Effect to fetch department summary data when dependencies change
  useEffect(() => {
    fetchDepartmentSummaryData()
  }, [selectedWorkspace?.id, startDate, endDate, periodViewType, selectedDepartmentId, selectedBusinessId, selectedMemberId, fetchDepartmentSummaryData])

  // (convertToTaskTreeNode / convertTaskItemToTreeNode 제거 — TaskDetailPanel이 자체 fetch + 변환)

  // Handle task row click (updated for TasksTable)
  // 패널이 자체 fetch하므로 부모에서 미리 GET 호출/변환 불필요
  const handleTaskRowClick = (taskTreeNode: TaskTreeNode) => {
    if (isActionButtonClicked) return
    if (!taskTreeNode || !taskTreeNode.id) return

    setSelectedTaskIdForDetail(taskTreeNode.id)
    setIsDetailPanelOpen(true)
  }

  // Handle task detail panel close
  const handleDetailPanelClose = () => {
    setIsDetailPanelOpen(false)
    setSelectedTaskIdForDetail(null)
  }

  // Handle task detail panel actions
  const handleTaskEdit = (task: TaskTreeNode) => {
    if (task.type === 'task' && task.originalData) {
      const taskData = task.originalData as any

      const editData = {
        id: task.id,
        name: task.name,
        taskSubgroupId: taskData.task_subgroup_id || '',
        taskSubgroupName: taskData.task_subgroup_name || '',
        taskGroupName: taskData.task_group_name || '',
        businessName: task.business_name || '',
        businessId: taskData.business_id || '',
        assigneeId: taskData.assignee?.id || '',
        assigneeName: taskData.assignee?.name || '',
        subAssigneeId: taskData.sub_assignee?.id || '',
        subAssigneeName: taskData.sub_assignee?.name || '',
        managerId: taskData.manager?.id || '',
        managerName: taskData.manager?.name || '',
        reviewerId: taskData.reviewer?.id || '',
        reviewerName: taskData.reviewer?.name || '',
        departmentId: taskData.department_id || '',
        departmentName: task.department_name || '',
        startAt: taskData.start_at || '',
        endAt: taskData.end_at || '',
        description: taskData.description || '',
        labels: taskData.labels || [],
        detailInfos: taskData.detail_infos || [],
        extraInfos: taskData.extra_infos || [],
        judgmentBasis: taskData.judgment_basis || '',
        // budget/performance 초기값을 편집 패널에 넘기기 위해 보관
        taskOriginal: taskData
      }

      setEditTaskData(editData)
      // 편집 패널 렌더 조건을 위해 id 유지
      setSelectedTaskIdForDetail(task.id)

      // Track which panel opened the edit BEFORE any panel state changes
      if (isBudgetDetailPanelOpen) {
        setEditSourcePanel('budget')
      } else if (isPerformanceDetailPanelOpen) {
        setEditSourcePanel('performance')
      } else {
        setEditSourcePanel('task')
      }

      setIsTaskEditPanelOpen(true)
      // 상세 패널은 열린 상태로 유지 - 닫지 않음
    }
  }

  const handleTaskDelete = (task: TaskTreeNode) => {
    console.warn('Task delete requested:', task)
    // TODO: Implement delete functionality
  }

  // Handle task item edit from TaskItemsTable
  const handleTaskItemEdit = (task: TaskTreeNode) => {
    if (task.type === 'task_item' && task.originalData) {
      const taskItemData = task.originalData as any

      setEditTaskItemData({
        id: task.id,
        name: task.name,
        taskId: taskItemData.task_id || '',
        taskName: taskItemData.task_name || '',
        taskSubgroupName: taskItemData.task_subgroup_name || '',
        businessName: task.business_name || '',
        startAt: taskItemData.start_at || '',
        endAt: taskItemData.end_at || '',
        output: taskItemData.output || ''
      })

      setIsTaskItemEditPanelOpen(true)
      // 상세 패널은 열린 상태로 유지 - setIsTaskItemDetailPanelOpen(false) 제거
    }
  }

  const handleTaskItemEditPanelClose = () => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
    // 상세 패널은 열린 상태 유지 - selectedTaskItem 제거하지 않음
  }

  const handleTaskItemEditSuccess = async () => {
    const editedTaskItemId = editTaskItemData?.id

    // 목록 재조회는 테이블이 트리거를 보고 스스로 수행한다
    setTaskItemRefreshTrigger(prev => prev + 1)

    // 패널이 자체 fetch로 최신 데이터를 다시 가져오도록 refreshKey 증가
    if (editedTaskItemId) {
      setTaskDetailRefreshKey(k => k + 1)
    }
    // 편집 패널만 닫기 (상세 패널은 열린 상태 유지)
  }

  const handleTaskEditPanelClose = () => {
    setIsTaskEditPanelOpen(false)

    // Only clear edit data for budget/performance contexts
    // Task list context needs editTaskData for the callback
    if (editSourcePanel === 'budget' || editSourcePanel === 'performance') {
      setEditTaskData(null)
      setEditSourcePanel(null)
    }
    // 상세 패널은 열린 상태 유지 - selectedTask 제거하지 않음
  }

  const handleTaskEditSuccess = async () => {
    // 세부실행과제 수정 성공 후: 상세 패널은 열린 상태 유지, 데이터만 새로고침
    const editedTaskId = editTaskData?.id

    try {
      if (editSourcePanel === 'budget') {
        // 예산 목록 — 테이블이 트리거를 보고 재조회
        setTaskBudgetRefreshTrigger(prev => prev + 1)

        // 패널이 자체 fetch
        if (editedTaskId) {
          setTaskDetailRefreshKey(k => k + 1)
        }

      } else if (editSourcePanel === 'performance') {
        // 성과 목록 — 테이블이 트리거를 보고 재조회
        setTaskPerformanceRefreshTrigger(prev => prev + 1)

        // 패널이 자체 fetch
        if (editedTaskId) {
          setTaskDetailRefreshKey(k => k + 1)
        }

      } else {
        // Task list context - trigger refresh and update detail panel
        setIsRefreshingTask(true)
        setTaskRefreshTrigger(prev => prev + 1)

        // Wait for TasksTable to call onDataRefreshed with updated data
        // The update will happen via handleTasksDataRefreshed callback
        // Note: editTaskData and editSourcePanel will be cleared in the callback
        // after detail panel is updated, NOT in finally block
      }
    } catch (error) {
      console.error('Failed to refresh after task edit:', error)
      // Redux에서 이미 에러 처리됨, 로그만 남김
    } finally {
      // Close edit panel
      setIsTaskEditPanelOpen(false)

      // Only clear edit data for budget/performance contexts
      // Task list context clears these in handleTasksDataRefreshed callback
      if (editSourcePanel === 'budget' || editSourcePanel === 'performance') {
        setEditTaskData(null)
        setEditSourcePanel(null)
      }
      // Note: For task list context, isRefreshingTask and editTaskData are cleared in handleTasksDataRefreshed callback
    }
  }

  // Callback from TasksTable when data is refreshed (세부실행과제 목록에서 호출)
  const handleTasksDataRefreshed = useCallback((tasks: any[]) => {
    // Only process if we're refreshing after edit
    if (isRefreshingTask && editTaskData?.id) {
      // 패널이 자체 fetch로 최신 데이터를 다시 가져오도록 refreshKey 증가
      const updatedTask = tasks.find((t: any) => t.id === editTaskData.id)
      if (updatedTask) {
        setTaskDetailRefreshKey(k => k + 1)
      }

      // Clear refresh state here (not in finally or close handlers for task list context)
      setIsRefreshingTask(false)
      setEditTaskData(null)
      setEditSourcePanel(null)
    }
  }, [isRefreshingTask, editTaskData])

  const handleTaskDetailSuccess = () => {
    // Refresh task data after successful operations
    fetchTaskData()
  }

  // Handle task item approval/cancel success - refresh task items data
  const handleTaskItemApprovalSuccess = useCallback(() => {
    // Refresh both task item list data and summary data after approval/cancel actions
    if (selectedWorkspace?.id) {
      // Reload task summary for 기간별 tab
      if (periodViewType === 'period') {
        dispatch(fetchTaskSummary({
          workspaceId: selectedWorkspace.id,
          departmentId: selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : null,
          businessId: selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : null,
          assigneeId: selectedMemberId && selectedMemberName !== '전체' ? selectedMemberId : null,
          startDate: startDate ? formatDateForAPI(startDate) : null,
          endDate: endDate ? formatDateForAPI(endDate) : null
        }))
      }

      // Reload monthly summary for 전체 tab
      if (periodViewType === 'all') {
        fetchMonthlySummary()
      }

      // 결재는 필터를 바꾸지 않으므로 트리거를 올려 목록을 다시 조회한다
      setTaskItemRefreshTrigger(prev => prev + 1)
    }
  }, [
    selectedWorkspace?.id,
    periodViewType,
    dispatch,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    selectedMemberId,
    selectedMemberName,
    startDate,
    endDate,
    fetchMonthlySummary,
    taskItemStatusFilter
  ])

  // Handle task item click from TaskItemsTable
  const handleTaskItemClick = (taskItem: TaskTreeNode) => {
    setSelectedTaskItemId(taskItem.id)
    setIsTaskItemDetailPanelOpen(true)
  }

  // Handle task item detail panel close
  const handleTaskItemDetailPanelClose = () => {
    setIsTaskItemDetailPanelOpen(false)
    setSelectedTaskItemId(null)
  }

  // Handle report button click from TaskItemsTable and Task List
  const handleReportClick = async (task: TaskTreeNode) => {
    // Set action button click flag to prevent row click
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)

    // Close any open detail panels to prevent double panels
    setIsDetailPanelOpen(false)
    setSelectedTaskIdForDetail(null)

    // Fetch report templates with business_id from the selected task
    if (selectedWorkspace?.id) {
      const businessId = task.originalData?.business_id || task.business_id
      const templates = await fetchReportTemplates(selectedWorkspace.id, businessId)

      // Try to fetch existing report data if templates are available
      if (templates && templates.length > 0) {
        const firstTemplate = templates[0]

        try {
          // Fetch existing report detail for the first template
          const reportDetail = await TaskReportService.appsTaskViewsGetTaskReportDetail({
            taskId: task.id,
            reportTemplateId: firstTemplate.key
          })

          // Open report panel — 패널이 자체 fetch하므로 fetchedReport 보관 불필요
          setReportPanelState({
            isOpen: true,
            selectedTask: task,
            selectedTemplate: { key: firstTemplate.key, name: firstTemplate.name },
          })
          void reportDetail
        } catch (error: any) {
          console.log('No existing report found or error fetching report:', error)
          // If 404 or error, open panel without report data (new report)
          setReportPanelState({
            isOpen: true,
            selectedTask: task,
            selectedTemplate: { key: firstTemplate.key, name: firstTemplate.name },
          })
        }
      } else {
        // No templates available, open panel without template
        setReportPanelState({
          isOpen: true,
          selectedTask: task,
          selectedTemplate: null,
        })
      }
    } else {
      // No workspace, open panel without data
      setReportPanelState({
        isOpen: true,
        selectedTask: task,
        selectedTemplate: null,
      })
    }
  }

  // Handle report detail panel close
  const handleReportDetailPanelClose = () => {
    setReportPanelState({
      isOpen: false,
      selectedTask: null,
      selectedTemplate: null,
    })
  }

  // (convertTaskTreeNodeToReportResponse 제거 — ReportDetailPanel이 자체 fetch)

  // Fetch report templates
  const fetchReportTemplates = useCallback(async (workspaceId: string, businessId?: string | null) => {
    if (!workspaceId) return null

    setIsReportTemplatesLoading(true)
    setReportTemplatesError(null)

    try {
      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId,
        businessId: businessId || undefined
      })
      setReportTemplates(response || [])
      return response || []  // Return templates for immediate use
    } catch (error: any) {
      console.error('Failed to fetch report templates:', error)
      setReportTemplatesError('보고서 템플릿 목록을 불러오는데 실패했습니다.')
      return null
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }, [])

  // Handle report template change — 패널이 reportTemplateKey prop 변경을 감지해 자체 refetch
  const handleReportTemplateChange = (templateKey: string, templateName: string) => {
    setReportPanelState(prev => ({
      ...prev,
      selectedTemplate: templateKey ? { key: templateKey, name: templateName } : null,
    }))
  }

  // Handle report update — 보고서 저장 후 알림용. 패널은 onReportUpdate 내부 콜 후 자체 refetch.
  // (이전엔 부모가 직접 GetTaskReportDetail 호출했으나, 이제 패널이 자체 처리)
  const handleReportUpdate = useCallback(() => {
    // 필요시 다른 목록 새로고침을 여기 추가 가능
  }, [])

  // Handle task edit from report panel
  const handleReportTaskEdit = useCallback((taskId: string) => {
    const selectedTask = reportPanelState.selectedTask
    if (!selectedTask) return

    // Close report panel first using flushSync to ensure state is updated immediately
    flushSync(() => {
      handleReportDetailPanelClose()
    })
    // Then open edit panel
    handleTaskEdit(selectedTask)
  }, [reportPanelState.selectedTask])

  // Handle task detail view from report panel
  const handleReportTaskDetail = useCallback((taskId: string) => {
    const selectedTask = reportPanelState.selectedTask
    if (!selectedTask) return

    // Close report panel first using flushSync to ensure state is updated immediately
    flushSync(() => {
      handleReportDetailPanelClose()
    })
    // Then open detail panel
    handleTaskRowClick(selectedTask)
  }, [reportPanelState.selectedTask])

  // Handle budget item click — 패널이 자체 fetch
  const handleBudgetItemClick = (taskId: string) => {
    if (!taskId) return
    setSelectedBudgetTaskId(taskId)
    setIsBudgetDetailPanelOpen(true)
  }

  // Handle budget detail panel close
  const handleBudgetDetailPanelClose = () => {
    setIsBudgetDetailPanelOpen(false)
    setSelectedBudgetTaskId(null)
  }

  // Handle performance item click — 패널이 자체 fetch
  const handlePerformanceItemClick = (taskId: string) => {
    if (!taskId) return
    setSelectedPerformanceTaskId(taskId)
    setIsPerformanceDetailPanelOpen(true)
  }

  // Handle performance detail panel close
  const handlePerformanceDetailPanelClose = () => {
    setIsPerformanceDetailPanelOpen(false)
    setSelectedPerformanceTaskId(null)
  }

  // Handle business summary row click
  const handleBusinessSummaryRowClick = (event: RowClickedEvent) => {
    // Don't apply filter if action button was just clicked
    if (isActionButtonClicked) {
      return
    }

    if (event.data) {
      const businessName = event.data.business_name
      const businessId = event.data.business_id

      // Check if clicking the same business (toggle behavior)
      if (businessId === selectedBusinessId) {
        // Clear filter
        handleBusinessSelect('', '전체')

        // Show alert
        setBusinessFilterAlertMessage('필터를 해제했습니다.')
        setShowBusinessFilterAlert(true)
      } else {
        // Apply business filter
        handleBusinessSelect(businessId, businessName)

        // Show success alert
        setBusinessFilterAlertMessage(`${businessName} 필터를 적용했습니다.`)
        setShowBusinessFilterAlert(true)
      }
    }
  }

  // Handle business detail panel close
  const handleBusinessDetailPanelClose = () => {
    setIsBusinessDetailPanelOpen(false)
    setSelectedBusinessSummary(null)
  }

  // Handle department summary row click
  const handleDepartmentSummaryRowClick = (event: RowClickedEvent) => {
    // Don't apply filter if action button was just clicked
    if (isActionButtonClicked) {
      return
    }

    if (event.data) {
      const departmentName = event.data.department_name
      const departmentId = event.data.department_id

      // Check if clicking the same department (toggle behavior)
      if (departmentId === selectedDepartmentId) {
        // Clear filter
        handleDepartmentSelect('', '전체')

        // Show alert
        setDepartmentFilterAlertMessage('필터를 해제했습니다.')
        setShowDepartmentFilterAlert(true)
      } else {
        // Apply department filter
        handleDepartmentSelect(departmentId, departmentName)

        // Show success alert
        setDepartmentFilterAlertMessage(`${departmentName} 필터를 적용했습니다.`)
        setShowDepartmentFilterAlert(true)
      }
    }
  }

  // Handle department detail panel close
  const handleDepartmentDetailPanelClose = () => {
    setIsDepartmentDetailPanelOpen(false)
    setSelectedDepartmentSummary(null)
  }

  // Row style for business summary table - highlight selected business
  const getBusinessRowStyle = useCallback((params: RowClassParams) => {
    if (selectedBusinessId &&
        selectedBusinessName !== '전체' &&
        params.data?.business_id === selectedBusinessId) {
      return {
        backgroundColor: designTokens.colors.secondary.light,
        borderLeft: `3px solid ${designTokens.colors.secondary.DEFAULT}`,
      }
    }
    return undefined
  }, [selectedBusinessId, selectedBusinessName])

  // Row style for department summary table - highlight selected department
  const getDepartmentRowStyle = useCallback((params: RowClassParams) => {
    if (selectedDepartmentId &&
        selectedDepartmentName !== '전체' &&
        params.data?.department_id === selectedDepartmentId) {
      return {
        backgroundColor: designTokens.colors.secondary.light,
        borderLeft: `3px solid ${designTokens.colors.secondary.DEFAULT}`,
      }
    }
    return undefined
  }, [selectedDepartmentId, selectedDepartmentName])

  // Handle business filter alert close
  const handleBusinessFilterAlertClose = () => {
    setShowBusinessFilterAlert(false)
    setBusinessFilterAlertMessage('')
  }

  // Handle department filter alert close
  const handleDepartmentFilterAlertClose = () => {
    setShowDepartmentFilterAlert(false)
    setDepartmentFilterAlertMessage('')
  }

  // Handle date range change from DateRangePicker
  // Only update when both dates are provided (complete selection)
  const handleDateRangeChange = (newStartDate: Date | null, newEndDate: Date | null) => {
    // Only update if both dates are provided (indicating a complete range selection)
    if (newStartDate && newEndDate) {
      setStartDate(newStartDate)
      setEndDate(newEndDate)
      // Trigger display dates update (localStorage was updated by DateRangePicker)
      setStorageUpdateTrigger(prev => prev + 1)
    }
  }

  // Get title text based on selected list type and period view
  const getListTitle = () => {
    switch (selectedListType) {
      case 'task':
        return periodViewType === 'all' ? '월별 세부실행과제 진행율' : '세부실행과제 목록'
      case 'work':
        return '수행업무 목록'
      case 'budget':
        return periodViewType === 'all' ? '월별 예산 집행률' : '예산 목록'
      case 'performance':
        return periodViewType === 'all' ? '월별 성과 달성률' : '성과 목록'
      default:
        return '세부실행과제 목록'
    }
  }

  // Helper functions for formatting
  const formatNumber = (value: number, unit?: string) => {
    if (unit === '원') {
      return `${new Intl.NumberFormat('ko-KR').format(value)}${unit}`
    }
    return `${new Intl.NumberFormat('ko-KR').format(value)}개`
  }

  // Progress Bar Cell Renderer - Based on Figma Design
  const ProgressBarRenderer = (params: any) => {
    const ratio = params.value
    if (typeof ratio !== 'number') return '-'

    return (
      <div className="flex items-center gap-3 h-full w-full">
        {/* Progress Bar - Figma Design: gray background with primary fill */}
        <div
          className="flex-1"
          style={{
            height: '22px',
            backgroundColor: '#D0D0D0', // Figma: text/gray-2 color
            borderRadius: '6px', // Figma: 6px border radius
            position: 'relative',
            overflow: 'hidden',
            minWidth: '200px', // 최소 너비 보장
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${ratio}%`,
              backgroundColor: '#2F4062', // Figma: color/primary
              borderRadius: '6px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>

      </div>
    )
  }

  // Item Name Cell Renderer
  const ItemNameRenderer = (params: any) => {
    return (
      <div className="flex items-center gap-2 h-full">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: designTokens.colors.gray[4] }}
        />
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.gray[6],
          }}
        >
          {params.value}
        </span>
      </div>
    )
  }

  // Column definitions for Summary DataGrid
  // 전체탭(periodViewType === 'all')에서만 셀 클릭 가능 — 그 때만 cursor/underline/primary 색상 적용
  const summaryColumnDefs: ColDef[] = useMemo(() => {
    const isClickable = periodViewType === 'all'
    const clickableCellStyle = {
      textAlign: 'right' as const,
      padding: '0 16px',
      cursor: isClickable ? 'pointer' : 'default',
      textDecoration: isClickable ? 'underline' : 'none',
      color: isClickable ? designTokens.colors.primary.DEFAULT : 'inherit',
    }
    return [
    {
      headerName: '항목명',
      field: 'itemName',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellRenderer: ItemNameRenderer,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
    },
    {
      headerName: '전체',
      field: 'totalFormatted',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellStyle: clickableCellStyle,
    },
    {
      headerName: '완료',
      field: 'completedFormatted',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellStyle: clickableCellStyle,
    },
    {
      headerName: '미완료',
      field: 'incompleteFormatted',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellStyle: clickableCellStyle,
    },
    {
      headerName: '비율',
      field: 'ratio',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      sortable: false,
      filter: false,
      valueFormatter: (params) => params.value !== null ? `${params.value}%` : '-',
      suppressSizeToFit: true,
    },
    {
      headerName: '그래프',
      field: 'ratio',
      colId: 'graph',
      flex: 1, // 남는 공간을 모두 차지
      minWidth: 280,
      cellRenderer: ProgressBarRenderer,
      sortable: false,
      filter: false,
      suppressSizeToFit: false, // flex 사용 시 필요
    },
    ]
  }, [periodViewType])

  // Action Button Cell Renderer for Business and Department Summary
  const ActionButtonRenderer = (params: ICellRendererParams) => {
    // Prevent row click on entire action cell
    const handleCellClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
    }

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation() // Prevent row click event
      e.preventDefault() // Prevent default behavior

      // Set flag to prevent row click
      setIsActionButtonClicked(true)
      setTimeout(() => setIsActionButtonClicked(false), 100)

      if (params.data) {
        // Check if it's business summary data
        if ('business_name' in params.data) {
          setSelectedBusinessSummary(params.data)
          setIsBusinessDetailPanelOpen(true)
        }
        // Check if it's department summary data
        else if ('department_name' in params.data) {
          setSelectedDepartmentSummary(params.data)
          setIsDepartmentDetailPanelOpen(true)
        }
      }
    }

    const handleListClick = (e: React.MouseEvent) => {
      e.stopPropagation() // Prevent row click event
      e.preventDefault() // Prevent default behavior

      // Set flag to prevent row click
      setIsActionButtonClicked(true)
      setTimeout(() => setIsActionButtonClicked(false), 100)

      if (params.data) {
        // 사업별 현황 행 → 해당 사업으로 스코프
        if ('business_name' in params.data) {
          setListViewPanel({
            scope: 'business',
            id: params.data.business_id,
            name: params.data.business_name,
          })
        }
        // 부서별 현황 행 → 해당 부서로 스코프
        else if ('department_name' in params.data) {
          setListViewPanel({
            scope: 'department',
            id: params.data.department_id,
            name: params.data.department_name,
          })
        }
      }
    }

    return (
      <div
        onClick={handleCellClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          height: '100%'
        }}
      >
        <Button
          size="xsmall"
          color="light"
          onClick={handleClick}
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px',
            fontWeight: 600,
            padding: '4px 12px',
            cursor: 'pointer',
          }}
        >
          상세보기
        </Button>
        <Button
          size="xsmall"
          color="light"
          onClick={handleListClick}
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px',
            fontWeight: 600,
            padding: '4px 12px',
            cursor: 'pointer',
          }}
        >
          목록보기
        </Button>
      </div>
    )
  }

  // Business summary column definitions
  const businessSummaryColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '사업명',
      field: 'business_name',
      flex: 2,
      minWidth: 300,
      sortable: true,
      filter: true,
      suppressSizeToFit: false,
    },
    {
      headerName: '액션',
      field: 'action',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: ActionButtonRenderer,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0'
      }
    },
    {
      headerName: '전체 수행업무',
      field: 'task_item.total.total_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '완료 수행업무',
      field: 'task_item.total.completed_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '수행업무 진행율',
      field: 'task_item.total.count_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '전체 세부실행과제',
      hide: !showTasks, // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 숨김
      field: 'task.total.total_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '완료 세부실행과제',
      hide: !showTasks, // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 숨김
      field: 'task.total.completed_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '세부실행과제 진행율',
      hide: !showTasks, // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 숨김
      field: 'task.total.count_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '전체 예산액',
      field: 'task_budget.total.total_target_figure',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}원`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '집행 예산액',
      field: 'task_budget.total.total_done_figure',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}원`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '예산 집행율',
      field: 'task_budget.total.figure_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '성과 과제(전체)',
      field: 'task_performance.total.total_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '성과 과제(달성)',
      field: 'task_performance.total.completed_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '성과 달성율',
      field: 'task_performance.total.count_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
  ], [showTasks])

  // Department summary column definitions
  const departmentSummaryColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '부서명',
      field: 'department_name',
      flex: 2,
      minWidth: 300,
      sortable: true,
      filter: true,
      suppressSizeToFit: false,
    },
    {
      headerName: '액션',
      field: 'action',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: ActionButtonRenderer,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0'
      }
    },
    {
      headerName: '전체 수행업무',
      field: 'task_item.total.total_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '완료 수행업무',
      field: 'task_item.total.completed_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '수행업무 진행율',
      field: 'task_item.total.count_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '전체 세부실행과제',
      hide: !showTasks, // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 숨김
      field: 'task.total.total_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '완료 세부실행과제',
      hide: !showTasks, // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 숨김
      field: 'task.total.completed_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '세부실행과제 진행율',
      hide: !showTasks, // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 숨김
      field: 'task.total.count_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '전체 예산액',
      field: 'task_budget.total.total_target_figure',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}원`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '집행 예산액',
      field: 'task_budget.total.total_done_figure',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}원`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '예산 집행율',
      field: 'task_budget.total.figure_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '성과 과제(전체)',
      field: 'task_performance.total.total_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '성과 과제(달성)',
      field: 'task_performance.total.completed_count',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value.toLocaleString()}개`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
    {
      headerName: '성과 달성율',
      field: 'task_performance.total.count_percentage',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: false,
      suppressSizeToFit: true,
      valueFormatter: (params) => params.value === null || params.value === undefined ? '-' : `${params.value}%`,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px'
      }
    },
  ], [showTasks])





  // Prepare data for DataGrid with incomplete formatting
  const gridData = useMemo(() =>
    summaryData.map(item => ({
      ...item,
      totalFormatted: formatNumber(item.total, item.unit),
      completedFormatted: formatNumber(item.completed, item.unit),
      incompleteFormatted: formatNumber(item.incomplete, item.unit),
    })), [summaryData],
  )

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Business Filter Alert - Fixed Position */}
      {showBusinessFilterAlert && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          <Alert
            variant="success"
            message={businessFilterAlertMessage}
            onClose={handleBusinessFilterAlertClose}
            autoClose={true}
            duration={3000}
          />
        </div>
      )}

      {/* Department Filter Alert - Fixed Position */}
      {showDepartmentFilterAlert && (
        <div
          style={{
            position: 'fixed',
            top: '70px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          <Alert
            variant="success"
            message={departmentFilterAlertMessage}
            onClose={handleDepartmentFilterAlertClose}
            autoClose={true}
            duration={3000}
          />
        </div>
      )}

      {/* Filter & Action Bar - Single Row */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{
          backgroundColor: designTokens.colors.gray[0],
        }}
      >
        {/* Date Range Picker - Conditional */}
        {periodViewType === 'period' && (
          <div className="flex-shrink-0">
            <DateRangePicker
              startDate={displayDates[0]}
              endDate={displayDates[1]}
              onDateRangeChange={handleDateRangeChange}
              timePeriod={selectedTimePeriod}
              onTimePeriodChange={handleTimePeriodChange}
              triggerMode="icon"
              showDateText={true}
              workspaceId={selectedWorkspace?.id}
            />
          </div>
        )}

        {/* Period Type Toggle */}
        <div className="inline-flex rounded-md shadow-sm h-10 flex-shrink-0" role="group">
          <button
            type="button"
            onClick={() => handlePeriodViewChange('period')}
            className="px-4 font-medium border rounded-l-md transition-colors h-10 whitespace-nowrap"
            style={{
              backgroundColor: periodViewType === 'period' ? designTokens.colors.primary.DEFAULT : designTokens.colors.white,
              color: periodViewType === 'period' ? designTokens.colors.white : designTokens.colors.gray[6],
              borderColor: designTokens.colors.gray[2],
              fontFamily: designTokens.typography.fontFamily.sans[0],
            }}
          >
            기간별
          </button>
          <button
            type="button"
            onClick={() => handlePeriodViewChange('all')}
            className="px-4 font-medium border rounded-r-md transition-colors h-10 whitespace-nowrap"
            style={{
              backgroundColor: periodViewType === 'all' ? designTokens.colors.primary.DEFAULT : designTokens.colors.white,
              color: periodViewType === 'all' ? designTokens.colors.white : designTokens.colors.gray[6],
              borderColor: designTokens.colors.gray[2],
              borderLeft: 'none',
              fontFamily: designTokens.typography.fontFamily.sans[0],
            }}
          >
            전체
          </button>
        </div>

        {/* Business Selection */}
        <div className="w-[200px] flex-shrink-0">
          <BusinessSelect
            workspaceId={selectedWorkspace?.id || ''}
            businesses={businesses}
            isLoading={businessLoading}
            businessesError={businessError}
            selectedBusinessId={selectedBusinessId}
            selectedBusinessName={selectedBusinessName || '[전체] 사업 선택'}
            onSelect={handleBusinessSelect}
            placeholder="[전체] 사업 선택"
            showAllOption={true}
          />
        </div>

        {/* Department Selection */}
        <div className="w-[200px] flex-shrink-0">
          <DepartmentSelect
            workspaceId={selectedWorkspace?.id || ''}
            selectedDepartmentId={selectedDepartmentId}
            selectedDepartmentName={selectedDepartmentName || '[전체] 부서 선택'}
            onSelect={handleDepartmentSelect}
            placeholder="[전체] 부서 선택"
            showAllOption={true}
          />
        </div>

        {/* Member Selection */}
        <div className="w-[200px] flex-shrink-0">
          <MemberSelect
            workspaceId={selectedWorkspace?.id || ''}
            selectedMemberId={selectedMemberId}
            selectedMemberName={selectedMemberName || '[전체] 담당자 선택'}
            onSelect={handleMemberSelect}
            placeholder="[전체] 담당자 선택"
            label="담당자"
            showAllOption={true}
          />
        </div>

        {/* Clear Filters Button - conditional */}
        {((selectedDepartmentId && selectedDepartmentName !== '전체') ||
          (selectedBusinessId && selectedBusinessName !== '전체') ||
          (selectedMemberId && selectedMemberName !== '전체')) && (
          <Button
            size="medium"
            color="white"
            onClick={clearAllFilters}
            className="h-10 px-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
          >
            필터 초기화
          </Button>
        )}
      </div>
      {/* Department/Business Status Section */}
      {groupedStatus ? (
        <StatusTables
          businesses={businessSummaryData}
          departments={departmentSummaryData}
          businessLoading={isLoadingBusinessSummary}
          departmentLoading={isLoadingDepartmentSummary}
          businessError={businessSummaryError}
          departmentError={departmentSummaryError}
          showTasks={showTasks}
          selectedBusinessId={selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : undefined}
          selectedDepartmentId={selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : undefined}
          // 줄 클릭 = 그 사업·부서로 필터 (다시 누르면 해제) — 기존 표와 같은 동작
          onBusinessRowClick={(item) => handleBusinessSummaryRowClick({ data: item } as RowClickedEvent)}
          onDepartmentRowClick={(item) => handleDepartmentSummaryRowClick({ data: item } as RowClickedEvent)}
          onOpenBusinessDetail={(item) => {
            setSelectedBusinessSummary(item)
            setIsBusinessDetailPanelOpen(true)
          }}
          onOpenDepartmentDetail={(item) => {
            setSelectedDepartmentSummary(item)
            setIsDepartmentDetailPanelOpen(true)
          }}
          onOpenList={(scope, id, name) => setListViewPanel({ scope, id, name })}
        />
      ) : (
      <div className="flex-shrink-0">
        {/* Business Summary Section */}
        <div className="mt-4">
          <h2
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '18px',
              fontWeight: 700,
              color: designTokens.colors.gray[6],
              marginBottom: '12px',
            }}
          >
            사업별 현황
          </h2>
          <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
            <div className="w-full">
              {businessSummaryError ? (
                <div
                  className="flex items-center justify-center h-32 text-center"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[4],
                  }}
                >
                  {businessSummaryError}
                </div>
              ) : (
                <DataGrid
                  columnDefs={businessSummaryColumnDefs}
                  rowData={businessSummaryData}
                  loading={isLoadingBusinessSummary}
                  headerHeight={40}
                  rowHeight={42}
                  height={40 + (10 * 42) + 60 + 2} // header + (10 rows * rowHeight) + pagination height + border
                  rowSelection="single"
                  animateRows={true}
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[10, 20, 50]}
                  paginationAutoPageSize={false}
                  onRowClicked={handleBusinessSummaryRowClick}
                  onGridReady={(params) => {
                    params.api.sizeColumnsToFit()
                  }}
                  // 선택된 필터와 연동된 row 하이라이트
                  getRowStyle={getBusinessRowStyle}
                  // 범위 선택 및 복사 기능 활성화
                  enableRangeSelection={true}
                  rangeSelectionExcludeColumns={['action']}
                  enableCellCopy={true}
                  suppressRowClickSelection={true}
                  rangeCopyFormatter={(colId, value) => {
                    // 진행율 컬럼 포맷팅
                    if (colId?.includes('percentage')) {
                      return value !== null && value !== undefined ? `${value}%` : '0%'
                    }
                    if (value === null || value === undefined) return '-'
                    return value.toString()
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Department Summary Section */}
        <div className="mt-6">
          <h2
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '18px',
              fontWeight: 700,
              color: designTokens.colors.gray[6],
              marginBottom: '12px',
            }}
          >
            부서별 현황
          </h2>
          <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
            <div className="w-full">
              {departmentSummaryError ? (
                <div
                  className="flex items-center justify-center h-32 text-center"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[4],
                  }}
                >
                  {departmentSummaryError}
                </div>
              ) : (
                <DataGrid
                  columnDefs={departmentSummaryColumnDefs}
                  rowData={departmentSummaryData}
                  loading={isLoadingDepartmentSummary}
                  headerHeight={40}
                  rowHeight={42}
                  height={40 + (10 * 42) + 60 + 2} // header + (10 rows * rowHeight) + pagination height + border
                  animateRows={true}
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[10, 20, 50]}
                  paginationAutoPageSize={false}
                  onRowClicked={handleDepartmentSummaryRowClick}
                  onGridReady={(params) => {
                    params.api.sizeColumnsToFit()
                  }}
                  // 선택된 필터와 연동된 row 하이라이트
                  getRowStyle={getDepartmentRowStyle}
                  // 범위 선택 및 복사 기능 활성화
                  enableRangeSelection={true}
                  rangeSelectionExcludeColumns={['action']}
                  enableCellCopy={true}
                  suppressRowClickSelection={true}
                  rangeCopyFormatter={(colId, value) => {
                    // 진행율 컬럼 포맷팅
                    if (colId?.includes('percentage')) {
                      return value !== null && value !== undefined ? `${value}%` : '0%'
                    }
                    if (value === null || value === undefined) return '-'
                    return value.toString()
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Summary Title Section */}
      <div
        className="mt-6 flex items-center gap-[10px] py-[18px] flex-shrink-0"
      >
        <div className="flex items-center justify-center gap-[18px]">
          <div className="flex items-center gap-[10px]">
            <h2
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-small'][0],
                fontWeight: 700,
                lineHeight: designTokens.typography.fontSize['title-small'][1].lineHeight,
                color: designTokens.colors.gray[6],
              }}
            >
              요약
              {((periodViewType === 'all' && monthlySummaryError) || (periodViewType === 'period' && summaryError)) && (
                <span style={{
                  fontSize: '14px',
                  color: '#dc2626',
                  marginLeft: '16px'
                }}>
                  - 데이터 로드 실패
                </span>
              )}
              {((periodViewType === 'all' && isMonthlySummaryLoading) || (periodViewType === 'period' && isSummaryLoading)) && (
                <span style={{
                  fontSize: '14px',
                  color: designTokens.colors.gray[4],
                  marginLeft: '16px'
                }}>
                  - 로딩 중...
                </span>
              )}
            </h2>
          </div>
        </div>
      </div>

      {/* Summary Table - DataGrid */}
      <div className="flex-shrink-0">
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          <div className="w-full"> {/* Full width container for responsive layout */}
            <DataGrid
              rowData={gridData}
              columnDefs={summaryColumnDefs}
              headerHeight={40}
              rowHeight={42}
              height={40 + (gridData.length * 42) + 2} // header + (rows * rowHeight) + border
              suppressRowClickSelection={true}
              rowSelection="single"
              animateRows={true}
              onCellClicked={handleSummaryCellClicked}
              getRowStyle={(params) => {
                if (params.node.rowIndex! % 2 === 0) {
                  return { backgroundColor: designTokens.colors.gray[0] }
                }
                return { backgroundColor: designTokens.colors.white }
              }}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: false,
                suppressSizeToFit: true,
              }}
              // 범위 선택 및 복사 기능 활성화
              enableRangeSelection={true}
              rangeSelectionExcludeColumns={['graph']}
              enableCellCopy={true}
              rangeCopyFormatter={(colId, value) => {
                // 비율 포맷팅
                if (colId === 'ratio') {
                  return value !== null && value !== undefined ? `${value}%` : '-'
                }
                // 기본값 처리
                if (value === null || value === undefined) return '-'
                return value.toString()
              }}
            />
          </div>
        </div>
      </div>

      {/* List Title Section - Based on Figma Design */}
      <div className="mt-6 flex-shrink-0">
        <div
          className="flex items-center justify-between gap-[10px] py-[10px]"
          style={{
            borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
          }}
        >
          {/* Left: List Title */}
          <div className="flex items-center gap-[18px]">
            <div className="flex items-center gap-[10px]">
              <h2
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '20px', // title/small from Figma
                  lineHeight: '1.193359375em',
                  fontWeight: 700,
                  color: '#1B1B1B', // text/gray-6 from Figma
                }}
              >
                {getListTitle()}
              </h2>
            </div>
          </div>

          {/* Right: Status Filter + Search + Tab Menu */}
          <div className="flex items-center gap-3">
            {/* Status Filter (only for work tab) */}
            {selectedListType === 'work' && (
              <div className="relative">
                <select
                  value={taskItemStatusFilter}
                  onChange={(e) => setTaskItemStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">전체</option>
                  <option value="incomplete">미완료 (담당자)</option>
                  <option value="assigned">미완료 (관리자)</option>
                  <option value="completed">완료 (관리자)</option>
                  <option value="reviewed">완료 (검토자)</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Status Filter (only for task tab) */}
            {selectedListType === 'task' && (
              <div className="relative">
                <select
                  value={taskStatusFilter}
                  onChange={(e) => setTaskStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">전체</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Status Filter (only for budget tab) */}
            {selectedListType === 'budget' && (
              <div className="relative">
                <select
                  value={budgetStatusFilter}
                  onChange={(e) => setBudgetStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">전체</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Status Filter (only for performance tab) */}
            {selectedListType === 'performance' && (
              <div className="relative">
                <select
                  value={performanceStatusFilter}
                  onChange={(e) => setPerformanceStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">전체</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Search Input and Button */}
            <div className="flex items-center gap-2">
              {/* Search Input */}
              <div className="relative w-[300px]">
                <div
                  className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1]
                  }}
                >
                  <Search
                    className="w-5 h-5"
                    style={{ color: designTokens.colors.gray[3] }}
                  />
                  <input
                    type="text"
                    placeholder="명칭으로 검색해주세요."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="flex-1 bg-transparent outline-none"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                      color: searchInput ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch()
                      }
                    }}
                  />
                </div>
              </div>

              {/* Search Button */}
              <Button
                color="dark"
                size="medium"
                onClick={handleSearch}
                className="h-10 px-[10px]"
              >
                검색
              </Button>
            </div>

            {/* Tab Menu */}
            <div
              className="flex"
              style={{
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >

            {/* 업무 Tab */}
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'work' ? '#2F4062' : '#EBEBEB',
                borderRight: '1px solid #BDBDBD',
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 600,
                color: selectedListType === 'work' ? '#EBEBEB' : '#707071',
              }}
              onClick={() => handleListTypeChange('work')}
            >
              수행업무
            </div>

            {/* 과제 Tab */}
            {showTasks && (
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'task' ? '#2F4062' : '#EBEBEB', // color/primary or text/gray-1
                borderRight: '1px solid #BDBDBD', // text/gray-3
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px', // body/large-bold from Figma
                fontWeight: 600,
                color: selectedListType === 'task' ? '#EBEBEB' : '#707071', // text/gray-1 or text/gray-4
              }}
              onClick={() => handleListTypeChange('task')}
            >
              세부실행과제
            </div>
            )}

            {/* 예산 Tab */}
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'budget' ? '#2F4062' : '#EBEBEB',
                borderRight: '1px solid #BDBDBD',
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 600,
                color: selectedListType === 'budget' ? '#EBEBEB' : '#707071',
              }}
              onClick={() => handleListTypeChange('budget')}
            >
              예산
            </div>

            {/* 성과 Tab */}
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'performance' ? '#2F4062' : '#EBEBEB',
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 600,
                color: selectedListType === 'performance' ? '#EBEBEB' : '#707071',
              }}
              onClick={() => handleListTypeChange('performance')}
            >
              성과
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Monthly Task Chart - 전체 탭에서 과제 탭이 선택되었을 때만 표시. 월별 봉/x축 월 라벨 클릭 시 해당 월 목록 오픈 */}
      {selectedListType === 'task' && periodViewType === 'all' && monthlySummary && (
        <div className="mt-6 flex-shrink-0">
          <div
            className="bg-white rounded-lg shadow-sm border"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월의 세부실행과제 목록을 사이드패널로 봅니다"
          >
            <div className="p-6">
              {(() => {
                const chartData = transformMonthlyDataToChart(monthlySummary.task.monthly)
                taskChartHandlerRef.current = buildChartClickHandler('task', chartData.monthKeys)
                const chartOptions = getChartOptions(chartData.categories, 'task', chartData.series, taskChartHandlerRef)

                return (
                  <Chart
                    type="line"
                    series={chartData.series}
                    options={chartOptions}
                    height={400}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Task Budget Chart - 전체 탭에서 예산 탭이 선택되었을 때만 표시 */}
      {selectedListType === 'budget' && periodViewType === 'all' && monthlySummary && (
        <div className="mt-6 flex-shrink-0">
          <div
            className="bg-white rounded-lg shadow-sm border"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월의 예산 목록을 사이드패널로 봅니다"
          >
            <div className="p-6">
              {(() => {
                const chartData = transformMonthlyTaskBudgetDataToChart(monthlySummary.task_budget.monthly)
                budgetChartHandlerRef.current = buildChartClickHandler('budget', chartData.monthKeys)
                const chartOptions = getChartOptions(chartData.categories, 'budget', chartData.series, budgetChartHandlerRef)

                return (
                  <Chart
                    type="line"
                    series={chartData.series}
                    options={chartOptions}
                    height={400}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Task Performance Chart - 전체 탭에서 성과 탭이 선택되었을 때만 표시 */}
      {selectedListType === 'performance' && periodViewType === 'all' && monthlySummary && (
        <div className="mt-6 flex-shrink-0">
          <div
            className="bg-white rounded-lg shadow-sm border"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월의 성과 목록을 사이드패널로 봅니다"
          >
            <div className="p-6">
              {(() => {
                const chartData = transformMonthlyTaskPerformanceDataToChart(monthlySummary.task_performance.monthly)
                performanceChartHandlerRef.current = buildChartClickHandler('performance', chartData.monthKeys)
                const chartOptions = getChartOptions(chartData.categories, 'performance', chartData.series, performanceChartHandlerRef)

                return (
                  <Chart
                    type="line"
                    series={chartData.series}
                    options={chartOptions}
                    height={400}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* 과제 목록 테이블 - 기간별 탭에서 과제 탭이 선택되었을 때만 표시 */}
      {selectedListType === 'task' && periodViewType === 'period' && (
        <div className="mt-6 flex-1 min-h-0">
          <TasksTable
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId}
            selectedDepartmentId={selectedDepartmentId}
            selectedMemberId={selectedMemberId}
            isVisible={true}
            onTaskClick={handleTaskRowClick}
            onReportClick={handleReportClick}
            isActionButtonClicked={isActionButtonClicked}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            refreshTrigger={taskRefreshTrigger}
            onDataRefreshed={handleTasksDataRefreshed}
            status={taskStatusFilter || undefined}
          />
        </div>
      )}

      {/* 수행업무 목록 테이블 - 기간별·전체 탭 모두 표시. 전체 탭은 기간 조건 없이 조회 */}
      {selectedListType === 'work' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskItemsTable
            refreshTrigger={taskItemRefreshTrigger}
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId}
            selectedDepartmentId={selectedDepartmentId}
            selectedMemberId={selectedMemberId}
            isVisible={true}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            onTaskItemClick={handleTaskItemClick}
            startDate={periodViewType === 'period' ? startDate : null}
            endDate={periodViewType === 'period' ? endDate : null}
            status={taskItemStatusFilter || undefined}
          />
        </div>
      )}

      {/* 예산 목록 테이블 - 기간별 탭에서 예산 탭이 선택되었을 때만 표시 */}
      {selectedListType === 'budget' && periodViewType === 'period' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskBudgetsTable
            refreshTrigger={taskBudgetRefreshTrigger}
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId}
            selectedDepartmentId={selectedDepartmentId}
            selectedMemberId={selectedMemberId}
            isVisible={true}
            startDate={periodViewType === 'period' ? startDate : null}
            endDate={periodViewType === 'period' ? endDate : null}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={budgetStatusFilter || undefined}
            onBudgetItemClick={handleBudgetItemClick}
            onReportClick={handleReportClick}
            isActionButtonClicked={isActionButtonClicked}
          />
        </div>
      )}

      {/* 성과 목록 테이블 - 기간별 탭에서만 표시 */}
      {selectedListType === 'performance' && periodViewType === 'period' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskPerformancesTable
            refreshTrigger={taskPerformanceRefreshTrigger}
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId}
            selectedDepartmentId={selectedDepartmentId}
            selectedMemberId={selectedMemberId}
            isVisible={true}
            startDate={periodViewType === 'period' ? startDate : null}
            endDate={periodViewType === 'period' ? endDate : null}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={performanceStatusFilter || undefined}
            onPerformanceItemClick={handlePerformanceItemClick}
            onReportClick={handleReportClick}
            isActionButtonClicked={isActionButtonClicked}
          />
        </div>
      )}

      {/* 다른 탭들은 나중에 구현 */}
      {selectedListType !== 'task' && selectedListType !== 'work' && selectedListType !== 'budget' && selectedListType !== 'performance' && (
        <div className="mt-6 flex-1 min-h-0">
          <div
            className="flex items-center justify-center h-32 text-center"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.gray[4],
            }}
          >
            {getListTitle()} 기능은 준비 중입니다.
          </div>
        </div>
      )}

      {/* Task Detail Panel — id만 전달, 패널이 자체 fetch */}
      <TaskDetailPanel
        isOpen={isDetailPanelOpen}
        onClose={handleDetailPanelClose}
        taskId={selectedTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
        onDelete={handleTaskDelete}
        onSuccess={handleTaskDetailSuccess}
      />

      {/* Task Item Detail Panel */}
      <TaskDetailPanel
        isOpen={isTaskItemDetailPanelOpen}
        onClose={handleTaskItemDetailPanelClose}
        taskId={selectedTaskItemId}
        taskType="task_item"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskItemEdit}
        onDelete={undefined}
        onSuccess={handleTaskItemApprovalSuccess}
      />

      {/* Budget Task Detail Panel */}
      <TaskDetailPanel
        isOpen={isBudgetDetailPanelOpen}
        onClose={handleBudgetDetailPanelClose}
        taskId={selectedBudgetTaskId}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
        onDelete={undefined}
        onSuccess={undefined}
      />

      {/* Performance Task Detail Panel */}
      <TaskDetailPanel
        isOpen={isPerformanceDetailPanelOpen}
        onClose={handlePerformanceDetailPanelClose}
        taskId={selectedPerformanceTaskId}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
        onDelete={undefined}
        onSuccess={undefined}
      />

      {/* Business Detail Panel */}
      <BusinessDetailPanel
        isOpen={isBusinessDetailPanelOpen}
        onClose={handleBusinessDetailPanelClose}
        selectedBusiness={selectedBusinessSummary}
        startDate={startDate}
        endDate={endDate}
        periodViewType={periodViewType}
      />

      {/* Department Detail Panel */}
      <DepartmentDetailPanel
        isOpen={isDepartmentDetailPanelOpen}
        onClose={handleDepartmentDetailPanelClose}
        selectedDepartment={selectedDepartmentSummary}
        startDate={startDate}
        endDate={endDate}
        periodViewType={periodViewType}
      />

      {/* Report Detail Panel — id/templateKey만 전달, 패널이 자체 fetch */}
      <ReportDetailPanel
        isOpen={reportPanelState.isOpen}
        onClose={handleReportDetailPanelClose}
        taskId={reportPanelState.selectedTask?.id ?? null}
        reportTemplateKey={reportPanelState.selectedTemplate?.key ?? null}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={selectedWorkspace?.id || ''}
        onReportTypeChange={handleReportTemplateChange}
        onReportUpdate={handleReportUpdate}
        onEditTask={handleReportTaskEdit}
        onTaskClick={handleReportTaskDetail}
      />

      {/* Task Item Edit Panel */}
      {editTaskItemData && (
        <TaskItemCreatePanel
          isOpen={isTaskItemEditPanelOpen}
          onClose={handleTaskItemEditPanelClose}
          taskId={editTaskItemData.taskId}
          taskName={editTaskItemData.taskName}
          taskSubgroupName={editTaskItemData.taskSubgroupName}
          businessName={editTaskItemData.businessName}
          onSuccess={handleTaskItemEditSuccess}
          editMode={true}
          taskItemId={editTaskItemData.id}
          initialTaskItemData={{
            name: editTaskItemData.name,
            startAt: editTaskItemData.startAt,
            endAt: editTaskItemData.endAt,
            output: editTaskItemData.output
          }}
        />
      )}

      {/* Task Edit Panel */}
      {editTaskData && (() => {
        // budget/performance 초기값은 editTaskData.taskOriginal에서 추출
        const taskOriginal = editTaskData.taskOriginal

        return (
          <TaskCreatePanel
            isOpen={isTaskEditPanelOpen}
            onClose={handleTaskEditPanelClose}
            taskSubgroupId={editTaskData.taskSubgroupId}
            taskSubgroupName={editTaskData.taskSubgroupName}
            taskGroupName={editTaskData.taskGroupName}
            businessName={editTaskData.businessName}
            businessId={editTaskData.businessId}
            onSuccess={handleTaskEditSuccess}
            editMode={true}
            taskId={editTaskData.id}
            initialTaskData={{
              name: editTaskData.name,
              assigneeId: editTaskData.assigneeId,
              assigneeName: editTaskData.assigneeName,
              subAssigneeId: editTaskData.subAssigneeId,
              subAssigneeName: editTaskData.subAssigneeName,
              managerId: editTaskData.managerId,
              managerName: editTaskData.managerName,
              reviewerId: editTaskData.reviewerId,
              reviewerName: editTaskData.reviewerName,
              departmentId: editTaskData.departmentId,
              departmentName: editTaskData.departmentName,
              startAt: editTaskData.startAt,
              endAt: editTaskData.endAt,
              description: editTaskData.description,
              budget: (taskOriginal as any)?.budget ? {
                id: (taskOriginal as any).budget.id || '',
                resultDescription: (taskOriginal as any).budget.result_description || '',
                items: ((taskOriginal as any).budget.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              performance: (taskOriginal as any)?.performance ? {
                id: (taskOriginal as any).performance.id || '',
                name: (taskOriginal as any).performance.name || '',
                unit: (taskOriginal as any).performance.unit || '',
                items: ((taskOriginal as any).performance.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              labels: editTaskData.labels,
              detailInfos: editTaskData.detailInfos,
              extraInfos: editTaskData.extraInfos,
              judgmentBasis: editTaskData.judgmentBasis
            }}
          />
        )
      })()}

      {/* 요약 그리드 셀 / 월별 차트 클릭 → 항목별 리스트 사이드패널 (한 번에 하나만 열림).
          공통 필터(워크스페이스/사업/부서/멤버) 그대로 + 차트 봉 클릭 시 해당 월 좁힘. */}
      {(() => {
        const override = summaryListPanel?.dateRangeOverride
        const panelStartDate = override ? override.start : null
        const panelEndDate = override ? override.end : null
        const commonProps = {
          onClose: closeSummaryListPanel,
          workspaceId: selectedWorkspace?.id || '',
          selectedBusinessId: selectedBusinessId || undefined,
          selectedBusinessName: selectedBusinessId ? (selectedBusinessName || undefined) : undefined,
          selectedDepartmentId: selectedDepartmentId || undefined,
          selectedDepartmentName: selectedDepartmentId ? (selectedDepartmentName || undefined) : undefined,
          selectedMemberId: selectedMemberId || undefined,
          selectedMemberName: selectedMemberId ? (selectedMemberName || undefined) : undefined,
          startDate: panelStartDate,
          endDate: panelEndDate,
        }
        return (
          <>
            <TaskItemsListPanel
              refreshTrigger={taskItemRefreshTrigger}
              {...commonProps}
              isOpen={summaryListPanel?.type === 'task_item'}
              title={summaryListPanel?.type === 'task_item' ? summaryListPanel.title : '수행업무 목록'}
              status={summaryListPanel?.type === 'task_item' ? resolveStatusFilter('task_item', summaryListPanel.statusBucket) : undefined}
              onTaskItemClick={(item) => handleTaskItemClick(item)}
            />
            <TasksListPanel
              {...commonProps}
              isOpen={summaryListPanel?.type === 'task'}
              title={summaryListPanel?.type === 'task' ? summaryListPanel.title : '세부실행과제 목록'}
              status={summaryListPanel?.type === 'task' ? resolveStatusFilter('task', summaryListPanel.statusBucket) : undefined}
              onTaskClick={(task) => handleTaskRowClick(task)}
              onReportClick={handleReportClick}
              isActionButtonClicked={isActionButtonClicked}
            />
            <TaskBudgetsListPanel
              refreshTrigger={taskBudgetRefreshTrigger}
              {...commonProps}
              isOpen={summaryListPanel?.type === 'budget'}
              title={summaryListPanel?.type === 'budget' ? summaryListPanel.title : '예산 목록'}
              onBudgetItemClick={(taskId) => handleBudgetItemClick(taskId)}
            />
            <TaskPerformancesListPanel
              refreshTrigger={taskPerformanceRefreshTrigger}
              {...commonProps}
              isOpen={summaryListPanel?.type === 'performance'}
              title={summaryListPanel?.type === 'performance' ? summaryListPanel.title : '성과 목록'}
              onPerformanceItemClick={(taskId) => handlePerformanceItemClick(taskId)}
            />
          </>
        )
      })()}

      {/* 사업별/부서별 현황 "목록보기" → 유형 통합(탭) 목록 사이드패널.
          클릭한 행의 사업/부서로 스코프를 잡고, 대시보드 상단 필터(담당자/기간)도 함께 적용. */}
      {(() => {
        const isBusinessScope = listViewPanel?.scope === 'business'
        const isDepartmentScope = listViewPanel?.scope === 'department'
        // 스코프 차원은 클릭한 행 값으로, 나머지 차원은 대시보드 상단 필터로 lock
        const lvBusinessId = isBusinessScope ? listViewPanel!.id : (selectedBusinessId || undefined)
        const lvBusinessName = isBusinessScope ? listViewPanel!.name : (selectedBusinessId ? (selectedBusinessName || undefined) : undefined)
        const lvDepartmentId = isDepartmentScope ? listViewPanel!.id : (selectedDepartmentId || undefined)
        const lvDepartmentName = isDepartmentScope ? listViewPanel!.name : (selectedDepartmentId ? (selectedDepartmentName || undefined) : undefined)
        return (
          <ListViewTabPanel
            isOpen={!!listViewPanel}
            onClose={closeListViewPanel}
            title={listViewPanel ? `${listViewPanel.name} 목록` : '목록'}
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={lvBusinessId}
            selectedBusinessName={lvBusinessName}
            selectedDepartmentId={lvDepartmentId}
            selectedDepartmentName={lvDepartmentName}
            selectedMemberId={selectedMemberId || undefined}
            selectedMemberName={selectedMemberId ? (selectedMemberName || undefined) : undefined}
            startDate={periodViewType === 'period' ? startDate : null}
            endDate={periodViewType === 'period' ? endDate : null}
            isActionButtonClicked={isActionButtonClicked}
            onTaskItemClick={(item) => handleTaskItemClick(item)}
            onTaskClick={(task) => handleTaskRowClick(task)}
            onBudgetItemClick={(taskId) => handleBudgetItemClick(taskId)}
            onPerformanceItemClick={(taskId) => handlePerformanceItemClick(taskId)}
            onReportClick={handleReportClick}
          />
        )
      })()}

    </div>
  )
}

export default DetailDashboardPage